import { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { QuoteData, QuoteTotals } from '../types';
import { generateQuotePDF } from '../../../utils/pdfGenerator';
import { Quote } from '../../customer-quote-review-portal/types';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteData: QuoteData;
  totals: QuoteTotals;
}

const PreviewModal = ({ isOpen, onClose, quoteData, totals }: PreviewModalProps) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);

      // Convert QuoteData to Quote format expected by PDF generator
      const pdfQuote: Quote = {
        id: quoteData.header.referenceCode.toLowerCase(),
        referenceCode: quoteData.header.referenceCode,
        customerName: quoteData.header.customerName,
        projectName: quoteData.header.projectName,
        status: quoteData.header.status,
        createdDate: quoteData.header.quoteDate,
        validUntil: quoteData.header.validUntil,
        customerInfo: {
          name: quoteData.header.customerName,
          company: quoteData.header.customerName,
          email: 'customer@example.com',
          phone: '+32 2 123 4567',
          address: 'Customer Address',
        },
        licenses: {
          included: quoteData.licenses.included,
          items: quoteData.licenses.lines.map((line) => ({
            id: line.id,
            group: line.groupName,
            productName: line.productName,
            unitPrice: line.unitPrice,
            quantity: line.quantity,
            monthlyAmount: line.monthlyAmount,
            annualAmount: line.annualAmount,
          })),
          monthlySubtotal: totals.licensesMonthly,
          annualSubtotal: totals.licensesAnnual,
        },
        implementation: {
          included: quoteData.implementation.included,
          items: quoteData.implementation.lines.map((line) => ({
            id: line.id,
            group: line.groupName,
            productName: line.productName,
            unitPrice: line.hourlyRate,
            quantity: line.hours,
            monthlyAmount: 0,
            annualAmount: line.totalAmount,
          })),
          monthlySubtotal: 0,
          annualSubtotal: totals.implementationTotal,
        },
        notes: quoteData.notes,
        totalMonthly: totals.licensesMonthly,
        totalAnnual: totals.licensesAnnual + totals.implementationTotal,
        totalExVAT: totals.totalExVAT,
      };

      await generateQuotePDF(pdfQuote);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-card rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Eye" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-text-primary">Quote Preview</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-150"
            aria-label="Close preview"
          >
            <Icon name="X" size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6 space-y-6">
          <div className="bg-primary/5 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
                    <path
                      d="M12 2L2 7L12 12L22 7L12 2Z"
                      fill="currentColor"
                      className="text-primary-foreground"
                    />
                    <path
                      d="M2 17L12 22L22 17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-primary-foreground"
                    />
                    <path
                      d="M2 12L12 17L22 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-primary-foreground"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-text-primary">Evoqia</h3>
                  <p className="text-sm text-text-secondary">Enterprise Solutions</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-secondary">Quote Reference</p>
                <p className="text-lg font-mono font-semibold text-text-primary">
                  {quoteData.header.referenceCode}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <p className="text-xs text-text-secondary mb-1">Customer</p>
                <p className="font-medium text-text-primary">{quoteData.header.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Project</p>
                <p className="font-medium text-text-primary">{quoteData.header.projectName}</p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Quote Date</p>
                <p className="font-medium text-text-primary">
                  {formatDate(quoteData.header.quoteDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Valid Until</p>
                <p className="font-medium text-text-primary">
                  {formatDate(quoteData.header.validUntil)}
                </p>
              </div>
            </div>
          </div>

          {quoteData.licenses.included && quoteData.licenses.lines.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Icon name="Package" size={20} className="text-primary" />
                Licenses
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-text-primary border border-border">
                        Product
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Unit Price
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Monthly
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Annual
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteData.licenses.lines.map((line) => (
                      <tr key={line.id}>
                        <td className="px-4 py-2 text-sm text-text-primary border border-border">
                          {line.productName}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-mono text-text-primary border border-border">
                          €{line.unitPrice.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-text-primary border border-border">
                          {line.quantity}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-mono text-text-primary border border-border">
                          €{line.monthlyAmount.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-mono text-text-primary border border-border">
                          €{line.annualAmount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {quoteData.implementation.included && quoteData.implementation.lines.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Icon name="Wrench" size={20} className="text-primary" />
                Implementation Services
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-text-primary border border-border">
                        Service
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Hourly Rate
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Hours
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-text-primary border border-border">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteData.implementation.lines.map((line) => (
                      <tr key={line.id}>
                        <td className="px-4 py-2 text-sm text-text-primary border border-border">
                          {line.productName}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-mono text-text-primary border border-border">
                          €{line.hourlyRate.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-text-primary border border-border">
                          {line.hours}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-mono text-text-primary border border-border">
                          €{line.totalAmount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-primary/5 p-6 rounded-lg">
            <div className="space-y-2">
              {quoteData.licenses.included && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Licenses (Monthly)</span>
                    <span className="font-mono text-text-primary">
                      €{totals.licensesMonthly.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Licenses (Annual)</span>
                    <span className="font-mono text-text-primary">
                      €{totals.licensesAnnual.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
              {quoteData.implementation.included && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Implementation Services</span>
                  <span className="font-mono text-text-primary">
                    €{totals.implementationTotal.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-border">
                <span className="font-semibold text-text-primary">Total Estimate (ex. VAT)</span>
                <span className="font-mono text-xl font-bold text-primary">
                  €{totals.totalExVAT.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {quoteData.notes && (
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-primary" />
                Notes & Terms
              </h3>
              <div className="bg-muted p-4 rounded-lg text-sm text-text-primary whitespace-pre-wrap">
                {quoteData.notes}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleDownloadPDF}
            loading={isGeneratingPDF}
            disabled={isGeneratingPDF}
          >
            Export PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;